import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { adminApi } from '@/services/adminApi';
import type {
  CostEvent,
  CostEventsFilters,
  CostEventsResponse,
} from '@/types/admin';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Search,
  X,
} from 'lucide-react';

/**
 * Row-level explorer for `AiCostEvent`. Surfaces every individual cost
 * write — token billing, image generation, recall.ai bots, e2b sandbox,
 * tavily/firecrawl/etc. third-party charges — with the full `meta` JSON
 * each entry carries (transcriptId, runtimeSec, credits, pricingSource).
 *
 * Filters mirror the backend's `getCostEvents` shape; one of them
 * (`pricingSource=missing_pricing`) is the same query the missing-pricing
 * banner uses, so this page doubles as the drill-down.
 */
const PAGE_SIZE = 50;

const numberOrEmpty = (n: number | null | undefined): string =>
  typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString() : '—';

const formatUsd = (n: number): string => {
  if (!(n > 0)) return '$0.00';
  if (n >= 0.01) return `$${n.toFixed(4)}`;
  // Sub-cent: show 6 decimals so micro-charges (cache reads, single tokens)
  // render as readable fixed-point instead of "$1.16e-4".
  return `$${n.toFixed(6)}`;
};

const formatTimestamp = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

// Pull deep-link filters out of the URL so /admin/cost-events?provider=…&model=…
// (used by the missing-pricing banner) lands directly on the filtered view.
const STRING_FILTER_KEYS = [
  'organizationId',
  'userId',
  'chatId',
  'messageId',
  'provider',
  'model',
  'feature',
  'purpose',
  'pricingSource',
  'startDate',
  'endDate',
] as const;

function filtersFromSearchParams(params: URLSearchParams): CostEventsFilters {
  const next: CostEventsFilters = { limit: PAGE_SIZE, offset: 0 };

  for (const key of STRING_FILTER_KEYS) {
    const value = params.get(key);
    if (value) (next as Record<string, unknown>)[key] = value;
  }

  const billingUnit = params.get('billingUnit');
  if (billingUnit === 'TOKENS' || billingUnit === 'IMAGE' || billingUnit === 'REQUEST') {
    next.billingUnit = billingUnit;
  }

  const billable = params.get('billable');
  if (billable === 'true' || billable === 'false') next.billable = billable === 'true';

  const isBackground = params.get('isBackground');
  if (isBackground === 'true' || isBackground === 'false') {
    next.isBackground = isBackground === 'true';
  }

  const period = params.get('period');
  if (period) {
    const parsed = Number(period);
    if (Number.isFinite(parsed) && parsed > 0) next.period = parsed;
  } else if (!next.startDate && !next.endDate) {
    next.period = 7;
  }

  return next;
}

export default function CostEventsPage() {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<CostEventsFilters>(() =>
    filtersFromSearchParams(searchParams),
  );
  const [data, setData] = useState<CostEventsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const result = await adminApi.getCostEvents(filters);
        if (!cancelled) setData(result);
      } catch (error) {
        if (!cancelled) {
          toast.error(
            error instanceof Error ? error.message : 'Failed to load cost events',
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  const setFilter = <K extends keyof CostEventsFilters>(
    key: K,
    value: CostEventsFilters[K],
  ) => {
    setFilters(prev => ({ ...prev, [key]: value, offset: 0 }));
  };

  const clearFilters = () => {
    setFilters({ period: 7, limit: PAGE_SIZE, offset: 0 });
  };

  const cacheHitRate = useMemo(() => {
    if (!data) return null;
    const input = data.summary.inputTokens;
    if (input <= 0) return null;
    return (data.summary.cacheReadTokens / input) * 100;
  }, [data]);

  const totalPages = data
    ? Math.max(1, Math.ceil(data.pagination.total / data.pagination.limit))
    : 0;
  const currentPage = data
    ? Math.floor(data.pagination.offset / data.pagination.limit) + 1
    : 1;

  const onPrevPage = () => {
    if (!data) return;
    const next = Math.max(0, (filters.offset ?? 0) - (filters.limit ?? PAGE_SIZE));
    setFilters(prev => ({ ...prev, offset: next }));
  };
  const onNextPage = () => {
    if (!data) return;
    if (data.pagination.offset + data.pagination.limit >= data.pagination.total) return;
    setFilters(prev => ({
      ...prev,
      offset: (prev.offset ?? 0) + (prev.limit ?? PAGE_SIZE),
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cost Events</h1>
          <p className="text-gray-500">
            Row-level inspector for every recorded cost — LLM tokens, images,
            and third-party services.
          </p>
        </div>
        <Button variant="outline" onClick={clearFilters}>
          <X className="h-4 w-4 mr-1.5" /> Clear filters
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Select
              label="Period"
              value={String(filters.period ?? 7)}
              onChange={e =>
                setFilter('period', e.target.value ? Number(e.target.value) : undefined)
              }
              options={[
                { value: '1', label: 'Last 1 day' },
                { value: '7', label: 'Last 7 days' },
                { value: '30', label: 'Last 30 days' },
                { value: '90', label: 'Last 90 days' },
                { value: '365', label: 'Last 365 days' },
              ]}
            />
            <Select
              label="Billing unit"
              value={filters.billingUnit ?? ''}
              onChange={e =>
                setFilter(
                  'billingUnit',
                  (e.target.value as CostEventsFilters['billingUnit']) || undefined,
                )
              }
              options={[
                { value: '', label: 'Any' },
                { value: 'TOKENS', label: 'Tokens' },
                { value: 'IMAGE', label: 'Image' },
                { value: 'REQUEST', label: 'Request' },
              ]}
            />
            <Select
              label="Billable"
              value={
                filters.billable === undefined
                  ? ''
                  : filters.billable
                    ? 'true'
                    : 'false'
              }
              onChange={e =>
                setFilter(
                  'billable',
                  e.target.value === '' ? undefined : e.target.value === 'true',
                )
              }
              options={[
                { value: '', label: 'Any' },
                { value: 'true', label: 'Billable' },
                { value: 'false', label: 'Non-billable' },
              ]}
            />
            <Select
              label="Background"
              value={
                filters.isBackground === undefined
                  ? ''
                  : filters.isBackground
                    ? 'true'
                    : 'false'
              }
              onChange={e =>
                setFilter(
                  'isBackground',
                  e.target.value === '' ? undefined : e.target.value === 'true',
                )
              }
              options={[
                { value: '', label: 'Any' },
                { value: 'false', label: 'Interactive' },
                { value: 'true', label: 'Background' },
              ]}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provider
              </label>
              <Input
                placeholder="anthropic, openai, recall.ai…"
                value={filters.provider ?? ''}
                onChange={e => setFilter('provider', e.target.value || undefined)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model
              </label>
              <Input
                placeholder="claude-sonnet-4-6, gpt-5-mini…"
                value={filters.model ?? ''}
                onChange={e => setFilter('model', e.target.value || undefined)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Feature
              </label>
              <Input
                placeholder="hardmode.agent_loop, web.tavily_search…"
                value={filters.feature ?? ''}
                onChange={e => setFilter('feature', e.target.value || undefined)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User ID
              </label>
              <Input
                placeholder="cuid…"
                value={filters.userId ?? ''}
                onChange={e => setFilter('userId', e.target.value || undefined)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organization ID
              </label>
              <Input
                placeholder="cuid…"
                value={filters.organizationId ?? ''}
                onChange={e => setFilter('organizationId', e.target.value || undefined)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chat ID
              </label>
              <Input
                placeholder="cuid…"
                value={filters.chatId ?? ''}
                onChange={e => setFilter('chatId', e.target.value || undefined)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pricing source
              </label>
              <Select
                value={filters.pricingSource ?? ''}
                onChange={e =>
                  setFilter('pricingSource', e.target.value || undefined)
                }
                options={[
                  { value: '', label: 'Any' },
                  { value: 'model_pricing', label: 'Has pricing row' },
                  { value: 'openrouter_usage', label: 'OpenRouter (paid)' },
                  { value: 'openrouter_byok', label: 'OpenRouter (BYOK upstream)' },
                  {
                    value: 'missing_pricing',
                    label: 'Missing pricing (zero-cost)',
                  },
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryStat label="Events" value={data?.summary.totalEvents.toLocaleString() ?? '—'} />
        <SummaryStat
          label="Total cost"
          value={data ? formatUsd(data.summary.totalCost) : '—'}
          accent="text-green-600"
        />
        <SummaryStat
          label="Total tokens"
          value={data ? data.summary.totalTokens.toLocaleString() : '—'}
        />
        <SummaryStat
          label="Cache hit rate"
          value={cacheHitRate !== null ? `${cacheHitRate.toFixed(1)}%` : '—'}
          accent="text-orange-600"
          subtitle={
            data
              ? `${data.summary.cacheReadTokens.toLocaleString()} read / ${data.summary.cacheWriteTokens.toLocaleString()} write`
              : undefined
          }
        />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            {data
              ? `${data.events.length} of ${data.pagination.total.toLocaleString()} events`
              : 'Events'}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onPrevPage}
              disabled={isLoading || !data || (filters.offset ?? 0) === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-600 min-w-[6rem] text-center">
              Page {currentPage} of {totalPages || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={onNextPage}
              disabled={
                isLoading ||
                !data ||
                data.pagination.offset + data.pagination.limit >=
                  data.pagination.total
              }
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && !data ? (
            <div className="text-center py-12 text-gray-500">Loading…</div>
          ) : !data || data.events.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              No cost events match the current filters.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Time</TableHead>
                  <TableHead>Provider / Model</TableHead>
                  <TableHead>Feature</TableHead>
                  <TableHead>User / Org</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  <TableHead className="text-right">Cache R / W</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Flags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.events.map(event => (
                  <CostEventRow
                    key={event.id}
                    event={event}
                    expanded={expandedId === event.id}
                    onToggle={() =>
                      setExpandedId(expandedId === event.id ? null : event.id)
                    }
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryStat({
  label,
  value,
  subtitle,
  accent,
}: {
  label: string;
  value: string;
  subtitle?: string;
  accent?: string;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className={`text-2xl font-semibold ${accent ?? 'text-gray-900'}`}>
          {value}
        </p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}

function CostEventRow({
  event,
  expanded,
  onToggle,
}: {
  event: CostEvent;
  expanded: boolean;
  onToggle: () => void;
}) {
  const cacheRead = event.cacheReadTokens ?? 0;
  const cacheWrite = event.cacheWriteTokens ?? 0;
  const meta = event.meta as Record<string, unknown> | null | undefined;
  const isMissingPricing = meta?.pricingSource === 'missing_pricing';
  const isOpenrouterEvent =
    meta?.pricingSource === 'openrouter_usage' || meta?.pricingSource === 'openrouter_byok';
  const openrouterMeta =
    isOpenrouterEvent && typeof meta?.openrouter === 'object'
      ? (meta.openrouter as {
          upstreamProvider?: string;
          servedModel?: string;
          upstreamCostUsd?: number;
          openrouterChargeUsd?: number;
          generationId?: string;
          isByok?: boolean;
        })
      : null;

  return (
    <>
      <TableRow
        className={`cursor-pointer hover:bg-gray-50 ${isMissingPricing ? 'bg-red-50' : ''}`}
        onClick={onToggle}
      >
        <TableCell>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </TableCell>
        <TableCell className="whitespace-nowrap text-sm text-gray-700">
          {formatTimestamp(event.createdAt)}
        </TableCell>
        <TableCell>
          <div className="font-medium text-gray-900">{event.model}</div>
          <div className="text-xs text-gray-500">
            {event.provider}
            {openrouterMeta?.upstreamProvider && (
              <span className="ml-1 text-purple-600">
                → {openrouterMeta.upstreamProvider}
              </span>
            )}
          </div>
        </TableCell>
        <TableCell>
          <div className="text-sm text-gray-800">{event.feature ?? '—'}</div>
          {event.purpose && (
            <div className="text-xs text-gray-500">{event.purpose}</div>
          )}
        </TableCell>
        <TableCell>
          {event.user ? (
            <div className="text-sm text-gray-800">
              {event.user.name || event.user.email}
            </div>
          ) : (
            <div className="text-sm text-gray-400">—</div>
          )}
          {event.organization && (
            <div className="text-xs text-gray-500">{event.organization.name}</div>
          )}
        </TableCell>
        <TableCell className="text-right text-sm">
          <div>{numberOrEmpty(event.totalTokens)}</div>
          <div className="text-xs text-gray-500">
            {numberOrEmpty(event.inputTokens)} in / {numberOrEmpty(event.outputTokens)} out
          </div>
        </TableCell>
        <TableCell className="text-right text-sm">
          <span className="text-orange-600">{cacheRead.toLocaleString()}</span>
          {' / '}
          <span className="text-amber-600">{cacheWrite.toLocaleString()}</span>
        </TableCell>
        <TableCell className="text-right text-sm font-medium text-green-600">
          {formatUsd(event.costUsd)}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex flex-col items-end gap-1">
            <span
              className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                event.billable
                  ? 'bg-green-50 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {event.billable ? 'Billable' : 'Non-billable'}
            </span>
            {event.isBackground && (
              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">
                Background
              </span>
            )}
            {isMissingPricing && (
              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                No pricing
              </span>
            )}
            {openrouterMeta && (
              <span
                className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700"
                title={[
                  openrouterMeta.openrouterChargeUsd != null
                    ? `OpenRouter charge: $${openrouterMeta.openrouterChargeUsd.toFixed(6)}`
                    : '',
                  openrouterMeta.upstreamCostUsd != null
                    ? `Upstream cost: $${openrouterMeta.upstreamCostUsd.toFixed(6)}`
                    : '',
                ]
                  .filter(Boolean)
                  .join('  •  ')}
              >
                {openrouterMeta.isByok ? 'OpenRouter (BYOK)' : 'OpenRouter'}
              </span>
            )}
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={9} className="bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-2">
              <div>
                <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
                  Identifiers
                </h4>
                <dl className="text-sm space-y-1">
                  <DefRow label="Event ID" value={event.id} mono />
                  <DefRow label="Chat ID" value={event.chatId ?? '—'} mono />
                  <DefRow label="Message ID" value={event.messageId ?? '—'} mono />
                  <DefRow label="Tool" value={event.toolName ?? '—'} />
                  <DefRow
                    label="Billing unit"
                    value={`${event.billingUnit} × ${event.quantity}`}
                  />
                </dl>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
                  Meta
                </h4>
                {meta && Object.keys(meta).length > 0 ? (
                  <pre className="text-xs bg-white border border-gray-200 rounded p-3 overflow-auto max-h-48">
                    {JSON.stringify(meta, null, 2)}
                  </pre>
                ) : (
                  <p className="text-sm text-gray-400">No meta recorded.</p>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function DefRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <dt className="text-gray-500 min-w-[6.5rem]">{label}</dt>
      <dd className={mono ? 'font-mono text-xs break-all' : ''}>{value}</dd>
    </div>
  );
}
